export enum ScoringMethod {
  LEGACY = "legacy",
  WEIGHTED = "weighted",
}

export interface MinMaxBand {
  min?: number | null;
  max?: number | null;
  score: number;
}

export interface GeographyBand {
  countryAlpha2s?: string[];
  regionNames?: string[];
  stateIds?: string[];
  score: number;
}

export type InclusionMethod = "ANY_PRESENT" | "ALL_PRESENT";

export interface IndustryBand {
  industryIds?: number[];
  sicUs1987Ids?: string[];
  naics2012Ids?: string[];
  score: number;
}

export interface GlobalPresenceBand {
  isMultiNational?: boolean;
  hqCountryCodes?: string[];
  hqRegions?: string[];
  subsidiaryLocations?: {
    countryCodes?: string[];
    regions?: string[];
    countMatching?: number;
    inclusionMethod?: InclusionMethod;
  };
  score: number;
}

// ── Tech Install band shapes ──────────────────────────────────────────────

export interface ProductIntensityBand {
  productIds: number[];
  min?: number;
  max?: number;
  score: number;
}

export interface ProductInstallAgeBand {
  productIds: number[];
  min?: number; // in months
  max?: number; // in months
  score: number;
}

export interface ProductPresenceBand {
  ids: number[];
  inclusionMethod?: InclusionMethod;
  departmentIds?: string[];
  roleIds?: string[];
  score: number;
}

// ── Intent band shapes ─────────────────────────────────────────────────────

export interface TopicsFilter {
  ids: string[];
  inclusionMethod?: InclusionMethod;
}

export interface SignalStrengthBand {
  signalScore?: { min?: number; max?: number };
  score: number;
  topics?: TopicsFilter;
}

export interface ContextTypeBand {
  contextTypeIds?: string[];
  score: number;
  topics?: TopicsFilter;
}

export interface BuyersJourneyBand {
  buyersJourneyIds?: string[];
  score: number;
  topics?: TopicsFilter;
}

export interface SignalLocationBand {
  location: {
    countryAlpha2s?: string[];
    regionNames?: string[];
    stateIds?: string[];
  };
  score: number;
  topics?: TopicsFilter;
}

// ── Tech Install band shapes ──────────────────────────────────────────────

export interface CategoryPenetrationBand {
  categoryId?: string;
  min?: number | null;
  max?: number | null;
  score: number;
}

export interface VendorPenetrationBand {
  vendorId?: number;
  min?: number | null;
  max?: number | null;
  score: number;
}

// ── Criterion key types ────────────────────────────────────────────────────

export interface CloudMaturityBand {
  fullTechStackRangeId?: string;
  newServicesRangeId?: string;
  /** UI-only display name — stripped by sanitize before save */
  displayName?: string;
  score: number;
}

export interface SpendBand {
  categoryId?: string;
  min?: number | null;
  max?: number | null;
  score: number;
}

export type FitCriterionKey =
  | "revenue"
  | "employees"
  | "geography"
  | "industry"
  | "globalPresence"
  | "productIntensity"
  | "productInstallAgeMonths"
  | "productCategoryPenetration"
  | "vendorPenetration"
  | "products"
  | "cloudMaturity"
  | "spend";

export type IntentCriterionKey =
  | "signalStrength"
  | "contextType"
  | "buyersJourney"
  | "location";

export type CriterionKey = FitCriterionKey | IntentCriterionKey;

// ── ScoringType — exactly one criterion key is populated per entry ─────────

export interface ScoringType {
  name: string;
  weight?: number;
  wasGenerated?: boolean;
  // Fit criteria
  revenue?: MinMaxBand[];
  employees?: MinMaxBand[];
  geography?: GeographyBand[];
  industry?: IndustryBand[];
  globalPresence?: GlobalPresenceBand[];
  // Tech Install criteria
  productIntensity?: ProductIntensityBand[];
  productInstallAgeMonths?: ProductInstallAgeBand[];
  productCategoryPenetration?: CategoryPenetrationBand[];
  vendorPenetration?: VendorPenetrationBand[];
  products?: ProductPresenceBand[];
  cloudMaturity?: CloudMaturityBand[];
  spend?: SpendBand[];
  // Intent criteria
  signalStrength?: SignalStrengthBand[];
  contextType?: ContextTypeBand[];
  buyersJourney?: BuyersJourneyBand[];
  location?: SignalLocationBand[];
}

export interface ScoringParameters {
  criteria: ScoringType[];
  scoringMethod: ScoringMethod;
  localizeInstalls?: boolean;
  version?: number;
}

export interface ScoringProfile {
  id: number;
  name: string;
  email: string;
  spog?: boolean;
  salesforce?: boolean;
  parameters: ScoringParameters;
  createdAt?: string;
  lastUpdated?: string;
}

export interface ScoringProfileCreate {
  name: string;
  email: string;
  spog: boolean;
  parameters: ScoringParameters;
}

export interface BandRow {
  summary: string;
  score: number;
  subtitle?: string;
}
